// src/pages/app/PayRates.js
import React, { useEffect, useMemo, useState } from "react";
import AdminPageHeader from "../../components/AdminPageHeader";
import useUserRole, { isSuperRole } from "../../hooks/useUserRole";
import { db } from "../../lib/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import "./PayRates.css";

const POSITIONS = [
  "Tech",
  "EMR",
  "EMT",
  "AEMT",
  "Paramedic",
  "RN",
  "PA",
  "APRN",
  "Physician",
  "Student",
  "Resident",
  "Volunteer",
  "THONE Emp",
  "Dispatcher",
  "Manager",
  "Director",
];

const NON_PAID_LABELS = ["Student", "Volunteer", "Resident", "THONE Emp"];

function isNumberLike(v) {
  if (v === "" || v === null || v === undefined) return false;
  const n = Number(v);
  return Number.isFinite(n);
}
function toMoneyString(v) {
  if (v === "" || v === null || v === undefined) return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2);
}

function normalizeMoneyInput(raw) {
  // Allow user to paste "$1,234.56" etc.
  let cleaned = String(raw || "").replace(/[^0-9.]/g, "");

  // Keep empty
  if (!cleaned) return "";

  // If user starts with ".", make it "0."
  if (cleaned.startsWith(".")) cleaned = "0" + cleaned;

  // Prevent multiple dots
  const parts = cleaned.split(".");
  if (parts.length > 2) {
    cleaned = parts[0] + "." + parts.slice(1).join("");
  }

  // Limit to 2 decimals while typing
  const [whole, dec] = cleaned.split(".");
  if (dec !== undefined) {
    return whole + "." + dec.slice(0, 2);
  }

  return whole;
}
export default function PayRates() {
  const { role } = useUserRole();
  const canAccess = isSuperRole(role);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savedMsg, setSavedMsg] = useState("");

  const [supervisorBump, setSupervisorBump] = useState("1");
  const [positions, setPositions] = useState(() => {
    const init = {};
    for (const p of POSITIONS) init[p] = NON_PAID_LABELS.includes(p) ? p : "0";
    return init;
  });

  // Load config
  useEffect(() => {
    if (!canAccess) return;
    let alive = true;

    (async () => {
      setErr("");
      try {
        const snap = await getDoc(doc(db, "appConfig", "payRates"));
        if (!alive) return;

        if (snap.exists()) {
          const data = snap.data() || {};
          const pos = data.positions || {};
          setSupervisorBump(String(data.supervisorBump ?? 1));

          // normalize into UI state
          const next = {};
          for (const p of POSITIONS) {
            const v = pos[p];
            if (NON_PAID_LABELS.includes(p)) {
              // Force these to be string labels
              next[p] = String(v || p);
            } else {
              next[p] = v === undefined || v === null ? "0" : String(v);
            }
          }
          setPositions(next);
        }
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setErr(e?.message || "Could not load pay rates.");
      }
    })();

    return () => {
      alive = false;
    };
  }, [canAccess]);

  const payload = useMemo(() => {
    const out = { positions: {}, supervisorBump: Number(supervisorBump || 0) };

    for (const p of POSITIONS) {
      const v = positions[p];

      if (NON_PAID_LABELS.includes(p)) {
        out.positions[p] = String(v || p);
      } else {
        out.positions[p] = isNumberLike(v) ? Number(v) : 0;
      }
    }

    return out;
  }, [positions, supervisorBump]);

  async function handleSave() {
    setErr("");
    setSavedMsg("");
    setBusy(true);
    try {
      // Write the whole doc (merge so you can expand later without clobbering unknown keys)
      await setDoc(doc(db, "appConfig", "payRates"), payload, { merge: true });
      setSavedMsg("Saved.");
      setTimeout(() => setSavedMsg(""), 1500);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Could not save pay rates.");
    } finally {
      setBusy(false);
    }
  }

  return (
  <div className="payRatesWrap">
    <AdminPageHeader title="Set Pay Rates" />

    {err ? <div className="tmError">{err}</div> : null}
    {savedMsg ? <div className="tmSuccess">{savedMsg}</div> : null}

  {/* 3-column pay rates panel */}
<div className="payRatesPanel">

  {/* Column A — Clinical rates */}
  <div className="adminModuleCard payRatesCard">
    <div className="adminModuleTitle">Clinical Pay Rates</div>
    <div className="adminModuleHint">Tech through Physician</div>

    <div className="payRatesTable">
      <div className="payRatesHeader">Position</div>
      <div className="payRatesHeader">Rate</div>

      {[
        "Tech",
        "EMR",
        "EMT",
        "AEMT",
        "Paramedic",
        "RN",
        "PA",
        "APRN",
        "Physician",
      ].map((p) => (
        <React.Fragment key={p}>
          <div className="payRatesPosition">{p}</div>

          <div className="payRatesMoneyCell">
            <span className="payRatesDollar">$</span>

            <input
              className="payRatesInput"
              inputMode="decimal"
              value={positions[p]}
              onChange={(e) => {
                const v = normalizeMoneyInput(e.target.value);
                setPositions((prev) => ({ ...prev, [p]: v }));
              }}
              onBlur={() => {
                setPositions((prev) => ({
                  ...prev,
                  [p]: toMoneyString(prev[p]),
                }));
              }}
              placeholder="0.00"
            />
          </div>
        </React.Fragment>
      ))}
    </div>
  </div>

  {/* Column B — Non-paid */}
  <div className="adminModuleCard payRatesCard">
    <div className="adminModuleTitle">Non-Paid Positions</div>
    <div className="adminModuleHint">
      Student • Volunteer • Resident • THONE Emp
    </div>

    <div className="payRatesTable">
      <div className="payRatesHeader">Position</div>
      <div className="payRatesHeader">Status</div>

      {["Student", "Volunteer", "Resident", "THONE Emp"].map((p) => (
        <React.Fragment key={p}>
          <div className="payRatesPosition">{p}</div>

          <div className="payRatesMoneyCell">
            <span className="payRatesDollar payRatesDollarSpacer">$</span>

            <select
              className="payRatesSelect payRatesSelectSized"
              value={positions[p]}
              onChange={(e) =>
                setPositions((prev) => ({
                  ...prev,
                  [p]: e.target.value,
                }))
              }
            >
              {NON_PAID_LABELS.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>
        </React.Fragment>
      ))}
    </div>
  </div>

  {/* Column C — Admin / Other */}
  <div className="adminModuleCard payRatesCard">
    <div className="adminModuleTitle">Other Roles</div>
    <div className="adminModuleHint">
      Dispatcher • Manager • Director
    </div>

    <div className="payRatesTable">
      <div className="payRatesHeader">Position</div>
      <div className="payRatesHeader">Rate</div>

      {["Dispatcher", "Manager", "Director"].map((p) => (
        <React.Fragment key={p}>
          <div className="payRatesPosition">{p}</div>

          <div className="payRatesMoneyCell">
            <span className="payRatesDollar">$</span>

            <input
              className="payRatesInput"
              inputMode="decimal"
              value={positions[p]}
              onChange={(e) => {
                const v = normalizeMoneyInput(e.target.value);
                setPositions((prev) => ({ ...prev, [p]: v }));
              }}
              onBlur={() => {
                setPositions((prev) => ({
                  ...prev,
                  [p]: toMoneyString(prev[p]),
                }));
              }}
              placeholder="0.00"
            />
          </div>
        </React.Fragment>
      ))}

      {/* Supervisor bump */}
      <div className="payRatesPosition">Supervisor Bump</div>

      <div className="payRatesMoneyCell">
        <span className="payRatesDollar">$</span>

        <input
          className="payRatesInput"
          inputMode="decimal"
          value={supervisorBump}
          onChange={(e) =>
            setSupervisorBump(normalizeMoneyInput(e.target.value))
          }
          onBlur={() =>
            setSupervisorBump(toMoneyString(supervisorBump))
          }
          placeholder="1.00"
        />
      </div>
    </div>

    <div className="tmTileHint" style={{ marginTop: 10 }}>
      Applied when role is <b>Supervisor</b>
    </div>
  </div>

</div>

<div className="payRatesActions">
  <button
    className="tmPrimaryBtn"
    onClick={handleSave}
    disabled={busy}
  >
    {busy ? "Saving…" : "Save Pay Rates"}
  </button>
</div>

</div>
);
}